import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { useSnackbar } from 'notistack'
import { useFormik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { FileDownload, PostAdd } from '@mui/icons-material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { cleanupRequestStatus, adminGetBillings } from 'features/billingsV2Slice'
import { Box, Button, Stack, Typography } from '@mui/joy'
import { formatMoney, handleErrorMessage, resolveAndDownloadBlob } from 'utils/helpers'
import { BILLING_STATUS } from 'utils/config'
import { billingsService } from 'services/billingService'
import routes from 'routes'
import useSearchParamValue from 'hooks/useSearchParamValue'
import Breadcrumbs from 'components/Breadcrumbs'
import usePagination from 'hooks/usePagination'
import FiltersWrapper from 'components/tables/FiltersWrapper'
import MainLayout from 'components/templates/MainLayout'
import PageLoader from 'components/PageLoader'
import FilterSearch from 'components/tables/FilterSearch'
import EnchantedTable from 'components/tables/EnchantedTable'
import DeleteModal from 'components/DeleteModal'
import TablePaginationDesktop from 'components/tables/TablePaginationDesktop'
import CustomDateRangePickerControl from 'components/forms/CustomDateRangePickerControl'

const getHeaders = () => [
  {
    label: 'Nr',
    propName: 'lp',
    type: 'route',
    route: routes.adminBillingsV2Details,
    routeReplace: ':billingId',
  },
  { label: 'Data dodania', propName: 'createdAt', type: 'date' },
  { label: 'Imię', propName: 'user.firstName' },
  { label: 'Nazwisko', propName: 'user.lastName' },
  {
    label: 'Ilość zadań',
    propName: 'computed2',
    type: 'computed',
    fn: (billing) => <Typography level="body-xs">{billing.taskIds.length}</Typography>,
  },
  {
    label: 'Wynagrodzenie brutto',
    propName: 'computed1',
    type: 'computed',
    fn: (billing) => <Typography level="body-xs">{formatMoney(billing.totalAmount)}</Typography>,
  },
  { label: 'Status', propName: 'status', type: 'status', statusObj: BILLING_STATUS },
]

const AdminBillingsV2View = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const pagination = usePagination()
  const { enqueueSnackbar } = useSnackbar()

  const [deleteBillingObject, setDeleteBillingObject] = useState(false)
  const [innerLoading, setInnerLoading] = useState(false)

  const [searchFilter, setSearchFilter] = useSearchParamValue('search', '')
  const [startDateFilter, setStartDateFilter, removeStartDateFilter] = useSearchParamValue('startDate', '')
  const [endDateFilter, setEndDateFilter, removeEndDateFilter] = useSearchParamValue('endDate', '')

  const { serverTime } = useSelector((s) => s.auth)
  const { billings, loading } = useSelector((s) => s.billingsV2)

  const formikFilters = useFormik({
    initialValues: {
      startDate: startDateFilter,
      endDate: endDateFilter,
    },
  })

  const params = useMemo(
    () => ({
      search: searchFilter || null,
      limit: pagination.values.perPage,
      page: pagination.values.currentPage,
      startDate: formikFilters.values.startDate ? dayjs(formikFilters.values.startDate).format('YYYY-MM-DD') : null,
      endDate: formikFilters.values.endDate ? dayjs(formikFilters.values.endDate).format('YYYY-MM-DD') : null,
    }),
    [
      searchFilter,
      pagination.values.perPage,
      pagination.values.currentPage,
      formikFilters.values.startDate,
      formikFilters.values.endDate,
    ]
  )

  const handleAcceptBilling = (billing) => {
    setInnerLoading(true)
    billingsService
      .adminConfirmBillingStatus(billing.id)
      .then(() => {
        enqueueSnackbar('Rozliczenie zostało zaakceptowane.', { variant: 'success' })
        dispatch(adminGetBillings(params))
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
      .finally(() => setInnerLoading(false))
  }

  const handleDeleteBilling = () => {
    setInnerLoading(true)
    billingsService
      .adminDeleteBilling(deleteBillingObject.id)
      .then(() => {
        enqueueSnackbar('Rozliczenie zostało usunięte.', { variant: 'success' })
        setDeleteBillingObject(false)
        dispatch(adminGetBillings(params))
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
      .finally(() => setInnerLoading(false))
  }

  const handleExportFilteredBillings = () => {
    setInnerLoading(true)
    const { search, startDate, endDate } = params
    billingsService
      .adminExportFilteredBilling({ search, startDate, endDate })
      .then((res) => {
        console.log(res)
        resolveAndDownloadBlob(res, `Rozlicznie zbiorcze ${dayjs(serverTime).format('DD_MM_YYYY')}`)
        enqueueSnackbar('Pobieranie pliku rozliczenia zbiorczego.', { variant: 'success' })
      })
      .catch(async (err) => {
        if (err.response && err.response.data && err.response.data.text) {
          const errorObj = JSON.parse((await err.response.data.text()) || {})
          if (errorObj.message) enqueueSnackbar(errorObj.message, { variant: 'error' })
          else enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
        } else enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
      .finally(() => setInnerLoading(false))
  }

  const handleExportBulkBillings = (selectedBillings) => {

    setInnerLoading(true)
    billingsService
      .adminExportBulkBilling(selectedBillings.map((b) => b.id))
      .then((res) => {
        console.log(res)
        resolveAndDownloadBlob(res, `Rozlicznie zbiorcze ${dayjs(serverTime).format('DD_MM_YYYY')}`)
        enqueueSnackbar('Pobieranie pliku rozliczenia zbiorczego.', { variant: 'success' })
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
      .finally(() => setInnerLoading(false))
  }

  const handleExportSingleBilling = (billing) => {
    setInnerLoading(true)

    billingsService
      .adminExportBilling(billing.id)
      .then((res) => {
        console.log(res)
        resolveAndDownloadBlob(res, `${billing.lp} ${billing.user.firstName} ${billing.user.lastName}`)
        enqueueSnackbar('Pobieranie pliku rozliczenia.', { variant: 'success' })
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar(handleErrorMessage(err, 'Coś poszło nie tak.'), { variant: 'error' })
      })
      .finally(() => setInnerLoading(false))
  }

  // It's stupid, but this prop MUST have separate useEffect
  useEffect(() => {
    if (params.startDate) setStartDateFilter(params.startDate)
    else removeStartDateFilter()
  }, [params.startDate])

  // It's stupid, but this prop MUST have separate useEffect
  useEffect(() => {
    if (params.endDate) setEndDateFilter(params.endDate)
    else removeEndDateFilter()
  }, [params.endDate])

  useEffect(() => {
    pagination.setPagination((prevPag) => ({ ...prevPag, ...billings.pagination }))
  }, [billings.pagination])

  useEffect(() => {
    dispatch(adminGetBillings(params))
    return () => dispatch(cleanupRequestStatus())
  }, [dispatch, params])

  if (loading)
    return (
      <MainLayout>
        <PageLoader />
      </MainLayout>
    )

  return (
    <MainLayout>
      <DeleteModal
        open={deleteBillingObject}
        handleDelete={handleDeleteBilling}
        loading={innerLoading}
        setObject={setDeleteBillingObject}
      >
        Czy jesteś pewny, ze chcesz usunąć rozliczenie:{' '}
        <Typography color="primary" fontWeight={500}>
          {deleteBillingObject.lp} - {deleteBillingObject.user?.firstName} {deleteBillingObject.user?.lastName}
        </Typography>
      </DeleteModal>

      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Breadcrumbs items={[{ label: 'Rozliczenia' }]} />
      </Box>
      <Box
        sx={{
          display: 'flex',
          mb: 1,
          gap: 1,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'start', sm: 'center' },
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <Typography level="h2" component="h1">
          Rozliczenia
        </Typography>
        <Stack rowGap={1} columnGap={2} direction="row" useFlexGap sx={{ flexWrap: 'wrap' }}>
          <Button
            color="primary"
            variant="outlined"
            size="sm"
            startDecorator={<FileDownload fontSize="small" />}
            loading={innerLoading}
            onClick={handleExportFilteredBillings}
          >
            Exportuj rozliczenia
          </Button>
          <Button
            component={RouterLink}
            to={routes.adminBillingsV2Create}
            color="primary"
            size="sm"
            startDecorator={<PostAdd fontSize="small" />}
          >
            Dodaj rozliczenie
          </Button>
        </Stack>
      </Box>

      <FiltersWrapper
        renderSearch={<FilterSearch label="Wyszukiwanie..." value={searchFilter} setValue={setSearchFilter} />}
        renderFilters={
          <>
            <CustomDateRangePickerControl
              nameOne="startDate"
              nameTwo="endDate"
              label="Filtruj po dacie"
              formik={formikFilters}
              small
              size="sm"
            />
          </>
        }
      />

      <EnchantedTable
        headers={getHeaders()}
        data={billings.data}
        offset={(pagination.values.currentPage - 1) * pagination.values.perPage}
        noDataText="Brak rozliczeń do wyświetlenia."
        actions={[
          {
            label: 'Zobacz rozliczenie',
            func: (billing) => navigate(routes.adminBillingsV2Details.replace(':billingId', billing.id)),
          },
          {
            label: 'Exportuj rozliczenie',
            color: 'primary',
            divider: true,
            loading: innerLoading,
            func: handleExportSingleBilling,
          },
          {
            label: 'Zaakceptuj rozliczenie',
            color: 'success',
            divider: true,
            loading: innerLoading,
            func: handleAcceptBilling,
            checkIfDisable: (billing) => billing.status !== BILLING_STATUS.pending.value,
          },
          {
            label: 'Usuń rozliczenie',
            color: 'danger',
            divider: true,
            loading: innerLoading,
            func: (billing) => setDeleteBillingObject(billing),
            checkIfDisable: (billing) => billing.status === BILLING_STATUS.confirmed.value,
          },
        ]}
        bulkActions={[
          {
            label: 'Exportuj rozliczenia',
            color: 'primary',
            func: (selectedBillings) => handleExportBulkBillings(selectedBillings),
          },
        ]}
      />
      <TablePaginationDesktop pagination={pagination} />
    </MainLayout>
  )
}

export default AdminBillingsV2View
